import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardBody, CardHeader, EmptyState, useToast } from '../../ui';
import {
  fetchSpend,
  downloadSpendCsv,
  fetchForecast,
  fetchBudgetAlerts,
} from '../../../lib/api/analytics';
import type { AnalyticsRange } from '../../../../../shared/types/analytics';
import type { SpendGroupBy, SpendRow } from '../../../../../shared/types/analytics';

// ---------------------------------------------------------------------------
// UX-02 — Spend tab (moved from pages/reports/spend.tsx). The rolling range
// now reads the hub's shared `?since=` instead of owning its own rolling
// selector; the calendar-period mode (period/from/to) stays Spend-only.
// The inner breakdown/forecast toggle is `?view=` (was `?tab=`, which now
// belongs to the hub's tab group).
// ---------------------------------------------------------------------------

const SPEND_METRIC_LABEL = 'Agent compute cost (model + compute) — not your invoice';

type View = 'breakdown' | 'forecast';
type PeriodMode = 'rolling' | 'period' | 'custom';

const GROUPBY_DIMS: SpendGroupBy[] = ['project', 'org', 'repo', 'agent', 'model', 'plan', 'account'];

const usd = (n: number): string => `$${n.toFixed(2)}`;

export const SpendPanel: React.FC<{
  since: AnalyticsRange;
  project: string | undefined;
  setProject: (p: string | undefined) => void;
  account?: string;
}> = ({ since, project, setProject, account }) => {
  const [search, setSearch] = useSearchParams();
  const toast = useToast();

  const view = (search.get('view') ?? 'breakdown') as View;
  const setView = (t: View) => {
    const next = new URLSearchParams(search);
    if (t === 'breakdown') next.delete('view');
    else next.set('view', t);
    setSearch(next, { replace: true });
  };

  const periodMode: PeriodMode = search.get('period')
    ? 'period'
    : search.get('from') || search.get('to')
      ? 'custom'
      : 'rolling';
  const period = search.get('period') ?? '';
  const from = search.get('from') ?? '';
  const to = search.get('to') ?? '';
  const groupByStr = search.get('groupBy') ?? 'project';
  const groupBy = useMemo(
    () => GROUPBY_DIMS.filter((d) => groupByStr.split(',').includes(d)),
    [groupByStr],
  );
  const org = search.get('org') ?? '';
  const repo = search.get('repo') ?? '';

  const setParam = (key: string, val: string | null) => {
    const next = new URLSearchParams(search);
    if (val) next.set(key, val);
    else next.delete(key);
    setSearch(next, { replace: true });
  };

  // Only period/from/to are local to Spend now — `since` is the hub's
  // shared filter, so switching modes here must never touch it (that would
  // clobber the range for every other tab). Picking "Billing period" or
  // "Custom range" before typing a value snaps back to "Rolling" on the
  // next render (documented in spend-panel.test.tsx) because periodMode is
  // derived purely from which of period/from/to are present.
  const setPeriodMode = (_mode: PeriodMode) => {
    const next = new URLSearchParams(search);
    next.delete('period');
    next.delete('from');
    next.delete('to');
    setSearch(next, { replace: true });
  };

  const toggleDim = (dim: SpendGroupBy) => {
    const set = new Set(groupBy);
    if (set.has(dim)) set.delete(dim);
    else set.add(dim);
    const ordered = GROUPBY_DIMS.filter((d) => set.has(d));
    setParam('groupBy', ordered.length > 0 ? ordered.join(',') : 'project');
  };

  const spendQuery = useMemo(
    () => ({
      since: periodMode === 'rolling' ? since : undefined,
      period: periodMode === 'period' ? period : undefined,
      from: periodMode === 'custom' ? from : undefined,
      to: periodMode === 'custom' ? to : undefined,
      groupBy,
      project: project || undefined,
      org: org || undefined,
      repo: repo || undefined,
      account,
    }),
    [periodMode, since, period, from, to, groupBy, project, org, repo, account],
  );

  const alertsQ = useQuery({
    queryKey: ['budget-alerts', account],
    queryFn: () => fetchBudgetAlerts({ account }),
  });

  const spendQ = useQuery({
    queryKey: ['spend', spendQuery],
    queryFn: () => fetchSpend(spendQuery),
    enabled: view === 'breakdown',
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadSpendCsv(spendQuery);
    } catch (err) {
      toast.error(`Export failed: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="spend-panel" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Spend</h2>
          <p className="text-sm text-slate-600 mt-1">{SPEND_METRIC_LABEL}</p>
        </div>
        {view === 'breakdown' && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            loading={exporting}
            data-testid="export-csv"
          >
            Export CSV
          </Button>
        )}
      </div>

      <BudgetAlertBanner items={alertsQ.data?.items ?? []} />

      <ViewBar view={view} setView={setView} />

      {view === 'breakdown' && (
        <BreakdownView
          periodMode={periodMode}
          setPeriodMode={setPeriodMode}
          since={since}
          period={period}
          setPeriod={(v) => setParam('period', v || null)}
          from={from}
          setFrom={(v) => setParam('from', v || null)}
          to={to}
          setTo={(v) => setParam('to', v || null)}
          groupBy={groupBy}
          toggleDim={toggleDim}
          project={project ?? ''}
          setProject={(v) => setProject(v || undefined)}
          org={org}
          setOrg={(v) => setParam('org', v || null)}
          repo={repo}
          setRepo={(v) => setParam('repo', v || null)}
          query={spendQ}
        />
      )}

      {view === 'forecast' && <ForecastTab account={account} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Budget alerts banner (FORGE-111, surfaced here per FORGE-304 P2-2)
// ---------------------------------------------------------------------------

const BudgetAlertBanner: React.FC<{ items: { id: string; severity: string; reason: string }[] }> = ({
  items,
}) => {
  if (items.length === 0) return null;
  return (
    <div data-testid="budget-alert-banner" className="space-y-1.5">
      {items.map((item) => (
        <div
          key={item.id}
          role="alert"
          className={
            'text-sm rounded-md px-3 py-2 border ' +
            (item.severity === 'critical'
              ? 'text-danger-700 bg-danger-50 border-danger-100'
              : 'text-warning-700 bg-warning-50 border-warning-100')
          }
        >
          {item.reason}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// View bar (was TabBar / ?tab=, now ?view=)
// ---------------------------------------------------------------------------

const VIEWS: { key: View; label: string }[] = [
  { key: 'breakdown', label: 'Breakdown' },
  { key: 'forecast', label: 'Forecast' },
];

const ViewBar: React.FC<{ view: View; setView: (t: View) => void }> = ({ view, setView }) => (
  <div
    className="inline-flex bg-white border border-slate-200 rounded-lg shadow-card p-0.5"
    role="tablist"
    aria-label="Spend report view"
  >
    {VIEWS.map((t) => (
      <button
        key={t.key}
        role="tab"
        aria-selected={view === t.key}
        type="button"
        onClick={() => setView(t.key)}
        data-testid={`tab-${t.key}`}
        className={
          'px-3.5 py-1.5 text-sm rounded-md font-medium transition-colors ' +
          (view === t.key ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-50')
        }
      >
        {t.label}
      </button>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Breakdown view (Phase 1 + Phase 2)
// ---------------------------------------------------------------------------

interface BreakdownViewProps {
  periodMode: PeriodMode;
  setPeriodMode: (m: PeriodMode) => void;
  since: AnalyticsRange;
  period: string;
  setPeriod: (v: string) => void;
  from: string;
  setFrom: (v: string) => void;
  to: string;
  setTo: (v: string) => void;
  groupBy: SpendGroupBy[];
  toggleDim: (d: SpendGroupBy) => void;
  project: string;
  setProject: (v: string) => void;
  org: string;
  setOrg: (v: string) => void;
  repo: string;
  setRepo: (v: string) => void;
  query: ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchSpend>>>>;
}

const BreakdownView: React.FC<BreakdownViewProps> = ({
  periodMode,
  setPeriodMode,
  since,
  period,
  setPeriod,
  from,
  setFrom,
  to,
  setTo,
  groupBy,
  toggleDim,
  project,
  setProject,
  org,
  setOrg,
  repo,
  setRepo,
  query,
}) => (
  <>
    <Card>
      <CardBody>
        <div className="space-y-3">
          <PeriodPicker
            mode={periodMode}
            setMode={setPeriodMode}
            since={since}
            period={period}
            setPeriod={setPeriod}
            from={from}
            setFrom={setFrom}
            to={to}
            setTo={setTo}
          />
          <GroupByPicker groupBy={groupBy} toggleDim={toggleDim} />
          <FilterRow project={project} setProject={setProject} org={org} setOrg={setOrg} repo={repo} setRepo={setRepo} />
        </div>
      </CardBody>
    </Card>

    {query.isLoading && <div className="text-sm text-slate-500">Loading spend…</div>}
    {query.isError && (
      <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
        {(query.error as Error).message}
      </div>
    )}

    {query.isSuccess && (
      <>
        {query.data.partialCoverage && (
          <div
            role="alert"
            data-testid="partial-coverage-warning"
            className="text-sm text-warning-700 bg-warning-50 border border-warning-100 rounded-md px-3 py-2"
          >
            Historical coverage for part of this period may be incomplete.
          </div>
        )}

        <div className="flex items-center gap-4" data-testid="spend-summary">
          <div className="text-sm text-slate-600">
            Grand total ({query.data.source})
          </div>
          <div className="text-2xl font-semibold text-slate-900 tabular-nums">
            {usd(query.data.grandTotalCostUsd)}
          </div>
        </div>

        {query.data.rows.length === 0 ? (
          <EmptyState
            testid="spend-empty"
            icon="💸"
            title="No spend in this period"
            description="Once agents run against a repo in scope, cost shows up here."
          />
        ) : (
          <SpendTable rows={query.data.rows} />
        )}
      </>
    )}
  </>
);

const PeriodPicker: React.FC<{
  mode: PeriodMode;
  setMode: (m: PeriodMode) => void;
  since: AnalyticsRange;
  period: string;
  setPeriod: (v: string) => void;
  from: string;
  setFrom: (v: string) => void;
  to: string;
  setTo: (v: string) => void;
}> = ({ mode, setMode, since, period, setPeriod, from, setFrom, to, setTo }) => (
  <div className="flex flex-wrap items-center gap-3">
    <div className="inline-flex bg-slate-100 rounded-md p-0.5" role="radiogroup" aria-label="Period mode">
      {(['rolling', 'period', 'custom'] as PeriodMode[]).map((m) => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={mode === m}
          data-testid={`period-mode-${m}`}
          onClick={() => setMode(m)}
          className={
            'px-2.5 py-1 text-xs rounded-md font-medium ' +
            (mode === m ? 'bg-white shadow-card text-slate-900' : 'text-slate-600')
          }
        >
          {m === 'rolling' ? 'Rolling' : m === 'period' ? 'Billing period' : 'Custom range'}
        </button>
      ))}
    </div>

    {mode === 'rolling' && (
      // The range itself is set from the hub's shared filter bar (top of
      // the page) — this just reflects what's active, no local buttons.
      <span className="text-xs text-slate-500" data-testid="spend-rolling-since">
        last {since === 'all' ? 'all time' : since}
      </span>
    )}

    {mode === 'period' && (
      <input
        type="text"
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
        placeholder="2026-06 / 2026-Q2 / 2026"
        data-testid="period-input"
        className="border border-slate-300 rounded-md text-xs px-2 py-1.5 font-mono"
      />
    )}

    {mode === 'custom' && (
      <div className="inline-flex items-center gap-1.5">
        <input
          type="date"
          value={from.slice(0, 10)}
          onChange={(e) => setFrom(e.target.value ? new Date(e.target.value).toISOString() : '')}
          data-testid="from-input"
          className="border border-slate-300 rounded-md text-xs px-2 py-1.5"
        />
        <span className="text-xs text-slate-400">to</span>
        <input
          type="date"
          value={to.slice(0, 10)}
          onChange={(e) => setTo(e.target.value ? new Date(e.target.value).toISOString() : '')}
          data-testid="to-input"
          className="border border-slate-300 rounded-md text-xs px-2 py-1.5"
        />
      </div>
    )}
  </div>
);

const GroupByPicker: React.FC<{ groupBy: SpendGroupBy[]; toggleDim: (d: SpendGroupBy) => void }> = ({
  groupBy,
  toggleDim,
}) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span className="text-xs text-slate-500 mr-1">Group by</span>
    {GROUPBY_DIMS.map((dim) => {
      const active = groupBy.includes(dim);
      const order = active ? groupBy.indexOf(dim) + 1 : null;
      return (
        <button
          key={dim}
          type="button"
          data-testid={`groupby-${dim}`}
          onClick={() => toggleDim(dim)}
          aria-pressed={active}
          className={
            'px-2 py-1 text-xs rounded-md border font-medium capitalize ' +
            (active
              ? 'bg-primary-50 text-primary-700 border-primary-200'
              : 'text-slate-600 border-slate-200 hover:bg-slate-50')
          }
        >
          {order ? `${order}. ` : ''}
          {dim}
        </button>
      );
    })}
  </div>
);

const FilterRow: React.FC<{
  project: string;
  setProject: (v: string) => void;
  org: string;
  setOrg: (v: string) => void;
  repo: string;
  setRepo: (v: string) => void;
}> = ({ project, setProject, org, setOrg, repo, setRepo }) => (
  <div className="flex flex-wrap items-center gap-3">
    <label className="text-xs text-slate-600 flex items-center gap-1.5">
      Project
      <input
        value={project}
        onChange={(e) => setProject(e.target.value)}
        data-testid="filter-project"
        placeholder="project id"
        className="border border-slate-300 rounded-md text-xs px-2 py-1.5 font-mono"
      />
    </label>
    <label className="text-xs text-slate-600 flex items-center gap-1.5">
      Org
      <input
        value={org}
        onChange={(e) => setOrg(e.target.value)}
        data-testid="filter-org"
        placeholder="github org"
        className="border border-slate-300 rounded-md text-xs px-2 py-1.5 font-mono"
      />
    </label>
    <label className="text-xs text-slate-600 flex items-center gap-1.5">
      Repo
      <input
        value={repo}
        onChange={(e) => setRepo(e.target.value)}
        data-testid="filter-repo"
        placeholder="org/repo"
        className="border border-slate-300 rounded-md text-xs px-2 py-1.5 font-mono"
      />
    </label>
  </div>
);

// ---------------------------------------------------------------------------
// Spend table — recursive, drill-through rows (FR-6/FR-7/FR-10)
// ---------------------------------------------------------------------------

const SpendTable: React.FC<{ rows: SpendRow[] }> = ({ rows }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden" data-testid="spend-table">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr className="text-left">
          <Th>Dimension</Th>
          <Th align="right">Cost</Th>
          <Th align="right">Share</Th>
          <Th align="right">Sessions</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <SpendRowGroup key={r.key} row={r} depth={0} />
        ))}
      </tbody>
    </table>
  </div>
);

const SpendRowGroup: React.FC<{ row: SpendRow; depth: number }> = ({ row, depth }) => {
  const hasChildren = !!row.children && row.children.length > 0;
  const [open, setOpen] = useState(depth === 0);
  return (
    <>
      <tr
        className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
        data-testid="spend-row"
        data-depth={depth}
      >
        <td className="px-4 py-2.5" style={{ paddingLeft: `${16 + depth * 20}px` }}>
          {hasChildren ? (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid="row-toggle"
              className="mr-1.5 text-slate-400 hover:text-slate-700 inline-block w-3"
              aria-expanded={open}
            >
              {open ? '▾' : '▸'}
            </button>
          ) : (
            <span className="mr-1.5 inline-block w-3" />
          )}
          {row.label}
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums">{usd(row.totalCostUsd)}</td>
        <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">
          {(row.sharePct * 100).toFixed(1)}%
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{row.sessionCount}</td>
      </tr>
      {hasChildren && open && row.children!.map((c) => <SpendRowGroup key={c.key} row={c} depth={depth + 1} />)}
    </>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th
    className={
      'px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold ' +
      (align === 'right' ? 'text-right' : '')
    }
  >
    {children}
  </th>
);

// ---------------------------------------------------------------------------
// Forecast tab (P2-4)
// ---------------------------------------------------------------------------

const ForecastTab: React.FC<{ account?: string }> = ({ account }) => {
  const forecastQ = useQuery({
    queryKey: ['forecast', account],
    queryFn: () => fetchForecast({ account }),
  });

  if (forecastQ.isLoading) return <div className="text-sm text-slate-500">Loading forecast…</div>;
  if (!forecastQ.data) return null;
  const f = forecastQ.data;

  return (
    <Card data-testid="forecast-card">
      <CardHeader>
        <div className="text-sm font-semibold text-slate-900">
          Run-rate forecast — {f.periodLabel}
        </div>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Spend to date" value={usd(f.spendToDateUsd)} />
          <Stat label="Elapsed" value={`${(f.elapsedFraction * 100).toFixed(0)}%`} />
          <Stat label="Projected total" value={usd(f.projectedTotalUsd)} />
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Simple linear projection (spend to date ÷ elapsed fraction of the month) — no seasonality.
        </p>
      </CardBody>
    </Card>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
    <div className="text-2xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
    <div className="text-xl font-semibold text-slate-900 mt-0.5 tabular-nums">{value}</div>
  </div>
);

export default SpendPanel;
